/**
 * Component Configuration Store
 *
 * This store manages the configuration/parameters for each component on the canvas.
 * It separates component configuration from visual representation, enabling cleaner
 * state management and easier migration from flowSpec.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

/**
 * Base configuration that all components share
 */
export interface BaseComponentConfig {
  id: string;
  type: string;
  name?: string;
  stepName: string;
  doubleBracesAlias?: string;
  __documentation__?: string;
}

interface AccountLabels {
  [key: string]: string;
}

/**
 * Configuration for regular components
 */
export interface RegularComponentConfig extends BaseComponentConfig {
  params?: Record<string, unknown>;
  accountLabel?: string;
  accountLabels?: AccountLabels;
  [key: string]: unknown; // Allow additional properties from connector schemas
}

/**
 * Configuration for compound components (block-execution, for-each, etc.)
 */
export interface CompoundComponentConfig extends BaseComponentConfig {
  params: {
    onProcess: string; // Track name
    onException: string; // Track name
    [key: string]: unknown;
  };
}

/**
 * Configuration for choice components
 */
export interface ChoiceCondition {
  target: string; // Track name
  simple?: string;
  jsonPath?: string;
  doubleBraces?: string;
  type: 'when' | 'otherwise';
  __documentation__?: string;
  /** Preserved for tool-router when conditions (MCP tool metadata) */
  toolData?: Record<string, unknown>;
}

export interface ChoiceComponentConfig extends BaseComponentConfig {
  when: ChoiceCondition[];
  otherwise?: string; // Track name
  __otherwiseDocumentation__?: string;
}

/**
 * Configuration for parallel execution components
 */
export interface ParallelExecution {
  target: string; // Track name (sourceId:targetId)
  description?: string;
  executionId: string;
  __documentation__?: string;
}

export interface ParallelComponentConfig extends BaseComponentConfig {
  params: {
    parallelExecutions: ParallelExecution[];
    [key: string]: unknown;
  };
}

/**
 * Union type for all component configurations
 */
export type ComponentConfig =
  | RegularComponentConfig
  | CompoundComponentConfig
  | ChoiceComponentConfig
  | ParallelComponentConfig;

/**
 * Type guard for compound components
 */
export function isCompoundComponentConfig(
  config: ComponentConfig
): config is CompoundComponentConfig {
  return (
    'params' in config &&
    typeof config.params === 'object' &&
    config.params !== null &&
    'onProcess' in config.params &&
    'onException' in config.params
  );
}

/**
 * Type guard for choice components
 */
export function isChoiceComponentConfig(
  config: ComponentConfig
): config is ChoiceComponentConfig {
  return 'when' in config;
}

/**
 * Type guard for parallel execution components
 */
export function isParallelComponentConfig(
  config: ComponentConfig
): config is ParallelComponentConfig {
  return (
    'params' in config &&
    typeof config.params === 'object' &&
    config.params !== null &&
    'parallelExecutions' in config.params
  );
}

/**
 * Component Configuration Store State
 */
export interface ComponentConfigState {
  // Core state
  configurations: Map<string, ComponentConfig>;

  // Actions - CRUD
  setConfig: (componentId: string, config: ComponentConfig) => void;
  setConfigs: (configs: ComponentConfig[]) => void;
  updateConfig: (
    componentId: string,
    updates: Partial<ComponentConfig>
  ) => void;
  removeConfig: (componentId: string) => void;
  removeConfigs: (componentIds: string[]) => void;
  getConfig: (componentId: string) => ComponentConfig | undefined;

  // Actions - Batch Updates
  batchUpdateConfigs: (
    updates: Array<{ componentId: string; updates: Partial<ComponentConfig> }>
  ) => void;
  batchRemoveConditions: (
    operations: Array<{ componentId: string; conditionTarget: string }>
  ) => void;
  batchRemoveExecutions: (
    operations: Array<{ componentId: string; executionTarget: string }>
  ) => void;

  // Actions - Specific Updates
  updateStepName: (componentId: string, stepName: string) => void;
  updateDoubleBracesAlias: (
    componentId: string,
    doubleBracesAlias: string
  ) => void;
  updateParams: (componentId: string, params: Record<string, unknown>) => void;
  updateDocumentation: (componentId: string, documentation: string) => void;

  // Actions - Choice Components
  addCondition: (componentId: string, condition: ChoiceCondition) => void;
  updateCondition: (
    componentId: string,
    conditionTarget: string,
    updates: Partial<ChoiceCondition>
  ) => void;
  removeCondition: (componentId: string, conditionTarget: string) => void;
  updateOtherwise: (componentId: string, otherwise: string) => void;

  // Actions - Parallel Execution Components
  addExecution: (componentId: string, execution: ParallelExecution) => void;
  updateExecution: (
    componentId: string,
    executionTarget: string,
    updates: Partial<ParallelExecution>
  ) => void;
  removeExecution: (componentId: string, executionTarget: string) => void;

  // Actions - Compound Components
  updateTrackReferences: (
    componentId: string,
    onProcess: string,
    onException: string
  ) => void;

  // Actions - Queries
  getConfigsByType: (type: string) => ComponentConfig[];
  getConfigsByName: (name: string) => ComponentConfig[];
  getAllDoubleBracesAliases: () => Set<string>;
  isDoubleBracesAliasInUse: (alias: string, excludeId?: string) => boolean;

  // Actions - Batch Operations
  clearConfigs: () => void;
  replaceConfigs: (configs: ComponentConfig[]) => void;
  reset: () => void;
}

/**
 * Create Component Configuration Store
 */
export const useComponentConfigStore = create<ComponentConfigState>()(
  devtools(
    immer((set, get) => ({
      // Initial state
      configurations: new Map(),

      // CRUD actions
      setConfig: (componentId, config) =>
        set(state => {
          state.configurations.set(componentId, config);
        }),

      setConfigs: configs =>
        set(state => {
          configs.forEach(config => {
            state.configurations.set(config.id, config);
          });
        }),

      updateConfig: (componentId, updates) =>
        set(state => {
          const config = state.configurations.get(componentId);
          if (config) {
            state.configurations.set(componentId, { ...config, ...updates });
          }
        }),

      removeConfig: componentId =>
        set(state => {
          state.configurations.delete(componentId);
        }),

      removeConfigs: componentIds =>
        set(state => {
          componentIds.forEach(id => {
            state.configurations.delete(id);
          });
        }),

      getConfig: componentId => get().configurations.get(componentId),

      // Batch updates
      batchUpdateConfigs: updates =>
        set(state => {
          updates.forEach(({ componentId, updates: configUpdates }) => {
            const config = state.configurations.get(componentId);
            if (config) {
              state.configurations.set(componentId, {
                ...config,
                ...configUpdates
              });
            }
          });
        }),

      batchRemoveConditions: operations =>
        set(state => {
          operations.forEach(({ componentId, conditionTarget }) => {
            const config = state.configurations.get(componentId);
            if (config && isChoiceComponentConfig(config)) {
              config.when = config.when.filter(
                c => c.target !== conditionTarget
              );
            }
          });
        }),

      batchRemoveExecutions: operations =>
        set(state => {
          operations.forEach(({ componentId, executionTarget }) => {
            const config = state.configurations.get(componentId);
            if (config && isParallelComponentConfig(config)) {
              config.params.parallelExecutions =
                config.params.parallelExecutions.filter(
                  e => e.target !== executionTarget
                );
            }
          });
        }),

      // Specific updates
      updateStepName: (componentId, stepName) =>
        set(state => {
          const config = state.configurations.get(componentId);
          if (config) {
            config.stepName = stepName;
          }
        }),

      updateDoubleBracesAlias: (componentId, doubleBracesAlias) =>
        set(state => {
          const config = state.configurations.get(componentId);
          if (config) {
            config.doubleBracesAlias = doubleBracesAlias;
          }
        }),

      updateParams: (componentId, params) =>
        set(state => {
          const config = state.configurations.get(componentId);
          if (config && 'params' in config) {
            config.params = { ...config.params, ...params };
          }
        }),

      updateDocumentation: (componentId, documentation) =>
        set(state => {
          const config = state.configurations.get(componentId);
          if (config) {
            /* eslint-disable-next-line no-underscore-dangle */
            config.__documentation__ = documentation;
          }
        }),

      // Choice component actions
      addCondition: (componentId, condition) =>
        set(state => {
          const config = state.configurations.get(componentId);
          if (config && isChoiceComponentConfig(config)) {
            config.when.push(condition);
          }
        }),

      updateCondition: (componentId, conditionTarget, updates) =>
        set(state => {
          const config = state.configurations.get(componentId);
          if (config && isChoiceComponentConfig(config)) {
            const conditionIndex = config.when.findIndex(
              c => c.target === conditionTarget
            );
            if (conditionIndex !== -1) {
              config.when[conditionIndex] = {
                ...config.when[conditionIndex],
                ...updates
              };
            }
          }
        }),

      removeCondition: (componentId, conditionTarget) =>
        set(state => {
          const config = state.configurations.get(componentId);
          if (config && isChoiceComponentConfig(config)) {
            config.when = config.when.filter(c => c.target !== conditionTarget);
          }
        }),

      updateOtherwise: (componentId, otherwise) =>
        set(state => {
          const config = state.configurations.get(componentId);
          if (config && isChoiceComponentConfig(config)) {
            config.otherwise = otherwise;
          }
        }),

      // Parallel execution actions
      addExecution: (componentId, execution) =>
        set(state => {
          const config = state.configurations.get(componentId);
          if (config && isParallelComponentConfig(config)) {
            config.params.parallelExecutions.push(execution);
          }
        }),

      updateExecution: (componentId, executionTarget, updates) =>
        set(state => {
          const config = state.configurations.get(componentId);
          if (config && isParallelComponentConfig(config)) {
            const executionIndex = config.params.parallelExecutions.findIndex(
              e => e.target === executionTarget
            );
            if (executionIndex !== -1) {
              config.params.parallelExecutions[executionIndex] = {
                ...config.params.parallelExecutions[executionIndex],
                ...updates
              };
            }
          }
        }),

      removeExecution: (componentId, executionTarget) =>
        set(state => {
          const config = state.configurations.get(componentId);
          if (config && isParallelComponentConfig(config)) {
            config.params.parallelExecutions =
              config.params.parallelExecutions.filter(
                e => e.target !== executionTarget
              );
          }
        }),

      // Compound component actions
      updateTrackReferences: (componentId, onProcess, onException) =>
        set(state => {
          const config = state.configurations.get(componentId);
          if (config && isCompoundComponentConfig(config)) {
            config.params.onProcess = onProcess;
            config.params.onException = onException;
          }
        }),

      // Query actions
      getConfigsByType: type => {
        const { configurations } = get();
        return Array.from(configurations.values()).filter(
          config => config.type === type
        );
      },

      getConfigsByName: name => {
        const { configurations } = get();
        return Array.from(configurations.values()).filter(
          config => config.name === name
        );
      },

      getAllDoubleBracesAliases: () => {
        const { configurations } = get();
        const aliases = new Set<string>();

        configurations.forEach(config => {
          if (config.doubleBracesAlias) {
            aliases.add(config.doubleBracesAlias);
          }
        });

        return aliases;
      },

      isDoubleBracesAliasInUse: (alias, excludeId) => {
        const { configurations } = get();

        return Array.from(configurations).some(
          ([id, config]) =>
            id !== excludeId && config.doubleBracesAlias === alias
        );
      },

      // Batch operations
      clearConfigs: () =>
        set(state => {
          state.configurations.clear();
        }),

      replaceConfigs: configs =>
        set(state => {
          state.configurations.clear();
          configs.forEach(config => {
            state.configurations.set(config.id, config);
          });
        }),

      reset: () =>
        set(state => {
          state.configurations = new Map();
        })
    })),
    { name: 'ComponentConfigStore' }
  )
);
